import React, { useState, useEffect, useRef } from 'react';
import { StageProps } from '../WizardContainer';
import { profileService } from '../../../services/api';
import { ProfilePhoto, ProfileVideo } from '../../../types/profile';

interface Stage6Props extends StageProps {
  step: number;
}

const Stage6Media: React.FC<Stage6Props> = ({
  onNext,
  onBack,
  isLoading,
  step,
}) => {
  const [photos, setPhotos] = useState<ProfilePhoto[]>([]);
  const [videos, setVideos] = useState<ProfileVideo[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadMedia = async () => {
      const profile = await profileService.getProfile();
      if (profile) {
        setPhotos(profile.photos || []);
        setVideos(profile.videos || []);
      }
    };
    loadMedia();
  }, []);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file
        if (!file.type.startsWith('image/')) {
          setError('Please select only image files');
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          setError('Image must be less than 10MB');
          continue;
        }

        // Simulate upload progress (in real app, use XMLHttpRequest or fetch with progress)
        setUploadProgress(((i + 1) / files.length) * 100);

        const newPhoto = await profileService.uploadPhoto(file);
        if (newPhoto) {
          setPhotos((prev) => [...prev, newPhoto]);
        }
      }
    } catch (err) {
      setError('Failed to upload photo. Please try again.');
      console.error('Photo upload error:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate video
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Video must be less than 50MB');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const newVideo = await profileService.uploadVideo(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (newVideo) {
        setVideos((prev) => [...prev, newVideo]);
      }
    } catch (err) {
      setError('Failed to upload video. Please try again.');
      console.error('Video upload error:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleSetPrimary = async (photoId: string) => {
    await profileService.setPhotoAsPrimary(photoId);
    setPhotos((prev) =>
      prev.map((p) => ({
        ...p,
        isPrimary: p.id === photoId,
      }))
    );
  };

  const handleDeletePhoto = async (photoId: string) => {
    await profileService.deletePhoto(photoId);
    setPhotos((prev) => prev.filter((p) => p.id !== photoId));
  };

  const handleDeleteVideo = async (videoId: string) => {
    await profileService.deleteVideo(videoId);
    setVideos((prev) => prev.filter((v) => v.id !== videoId));
  };

  const handleNext = async () => {
    onNext({ photos, videos });
  };

  const renderStep1 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Add Photos</h3>
      <p className="wizard-step-subtitle">
        Upload your best photos to make your profile stand out
      </p>

      <div className="wizard-photo-grid">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className={`wizard-photo-item ${photo.isPrimary ? 'primary' : ''}`}
          >
            <img src={photo.thumbnailUrl || photo.mediumUrl} alt="Profile" />
            <div className="wizard-photo-overlay">
              {!photo.isPrimary && (
                <button
                  type="button"
                  className="wizard-photo-action"
                  onClick={() => handleSetPrimary(photo.id)}
                  title="Set as primary"
                >
                  ★
                </button>
              )}
              <button
                type="button"
                className="wizard-photo-action danger"
                onClick={() => handleDeletePhoto(photo.id)}
                title="Delete"
              >
                ✕
              </button>
            </div>
            {photo.isPrimary && (
              <span className="wizard-photo-badge">Primary</span>
            )}
          </div>
        ))}

        {photos.length < 9 && (
          <button
            type="button"
            className="wizard-photo-add"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <div className="wizard-upload-progress">
                <span>{Math.round(uploadProgress)}%</span>
              </div>
            ) : (
              <>
                <span className="add-icon">+</span>
                <span>Add Photo</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handlePhotoUpload}
        style={{ display: 'none' }}
      />

      {error && <p className="wizard-error">{error}</p>}

      <div className="wizard-media-tips">
        <h4>Photo Tips</h4>
        <ul>
          <li>Use recent photos that clearly show your face</li>
          <li>Include a mix of close-ups and full-body shots</li>
          <li>Show yourself doing activities you enjoy</li>
          <li>Avoid group photos as your primary image</li>
        </ul>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Add Videos (Optional)</h3>
      <p className="wizard-step-subtitle">
        Videos help others get to know the real you
      </p>

      <div className="wizard-video-grid">
        {videos.map((video) => (
          <div key={video.id} className="wizard-video-item">
            <video src={video.videoUrl} controls />
            <div className="wizard-video-overlay">
              <button
                type="button"
                className="wizard-video-action danger"
                onClick={() => handleDeleteVideo(video.id)}
                title="Delete"
              >
                ✕
              </button>
            </div>
            <span className="wizard-video-duration">
              {Math.floor(video.durationSeconds / 60)}:
              {String(video.durationSeconds % 60).padStart(2, '0')}
            </span>
          </div>
        ))}

        {videos.length < 3 && (
          <button
            type="button"
            className="wizard-video-add"
            onClick={() => videoInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <div className="wizard-upload-progress">
                <span>{Math.round(uploadProgress)}%</span>
              </div>
            ) : (
              <>
                <span className="add-icon">▶</span>
                <span>Add Video</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoUpload}
        style={{ display: 'none' }}
      />

      {error && <p className="wizard-error">{error}</p>}

      <div className="wizard-media-tips">
        <h4>Video Tips</h4>
        <ul>
          <li>Keep videos under 30 seconds for best engagement</li>
          <li>Introduce yourself or share something interesting</li>
          <li>Good lighting and clear audio make a difference</li>
          <li>Be authentic - let your personality shine</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="wizard-stage">
      {step === 1 ? renderStep1() : renderStep2()}

      <div className="wizard-stage-actions">
        <button
          type="button"
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
          disabled={isLoading || uploading}
        >
          Back
        </button>
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={isLoading || uploading}
        >
          {isLoading ? 'Saving...' : step === 2 ? 'Complete Stage' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Stage6Media;
